import React, { forwardRef } from 'react';
import {
  LinearProgress as MuiLinearProgress,
  LinearProgressProps as MuiLinearProgressProps,
} from '@mui/material';

/**
 * Linear progress bar component for indicating loading states and progress.
 *
 * Wraps Material UI's LinearProgress component with TreePlotter theme integration
 * and enhanced accessibility defaults.
 *
 * @example
 * // Indeterminate (default) - most common for unknown duration
 * <LinearProgress />
 *
 * @example
 * // Determinate with value - most common for known progress
 * <LinearProgress variant="determinate" value={75} />
 *
 * @example
 * // With custom color
 * <LinearProgress color="secondary" />
 *
 * @example
 * // Buffer variant (less common - for streaming/buffering scenarios)
 * <LinearProgress variant="buffer" value={60} valueBuffer={80} />
 */
export interface LinearProgressProps extends MuiLinearProgressProps {
  /**
   * Accessible label for the progress indicator.
   * If not provided, defaults to:
   * - "Loading" for indeterminate variant
   * - "Progress: X%" for determinate variant
   * - "Buffering" for buffer variant
   * - "Querying" for query variant
   */
  'aria-label'?: string;
}

/**
 * Linear progress indicator showing loading or task completion.
 *
 * Supports multiple variants:
 * - **indeterminate** (default): Continuous animation for unknown duration - most commonly used
 * - **determinate**: Shows specific progress value (0-100) - commonly used for uploads, downloads
 * - **buffer**: Shows buffering with primary and secondary values - less common, for streaming
 * - **query**: Query-in-progress state - rarely used
 *
 * **Accessibility**: Automatically provides sensible aria-label defaults based on variant and value.
 */
/**
 * Clamps a value between a minimum and maximum.
 */
const clamp = (value: number | undefined, min: number, max: number): number | undefined => {
  if (value === undefined) return undefined;
  return Math.max(min, Math.min(max, value));
};

export const LinearProgress = forwardRef<HTMLDivElement, LinearProgressProps>(
  ({ variant = 'indeterminate', value, valueBuffer, 'aria-label': ariaLabel, ...props }, ref) => {
    // Clamp and validate incoming progress values to ensure they are within valid range (0-100)
    const clampedValue = clamp(value, 0, 100);
    const clampedValueBuffer = clamp(valueBuffer, 0, 100);

    // Provide sensible accessibility defaults
    const defaultAriaLabel = React.useMemo(() => {
      if (ariaLabel) return ariaLabel;

      switch (variant) {
        case 'determinate':
          return clampedValue !== undefined ? `Progress: ${Math.round(clampedValue)}%` : 'Progress';
        case 'buffer':
          return 'Buffering';
        case 'query':
          return 'Querying';
        case 'indeterminate':
        default:
          return 'Loading';
      }
    }, [variant, clampedValue, ariaLabel]);

    return (
      <MuiLinearProgress
        ref={ref}
        variant={variant}
        value={clampedValue}
        valueBuffer={clampedValueBuffer}
        aria-label={defaultAriaLabel}
        {...props}
      />
    );
  }
);

LinearProgress.displayName = 'LinearProgress';
